import React, { useState, useEffect } from "react";
import "./ApplyWorkFromHome.css";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

const ApplyWorkFromHome = () => {
  const today = new Date().toISOString().split("T")[0];

  /* ================= STATE ================= */

  const [wfhData, setWfhData] = useState({
    startDate: "",
    endDate: "",
    reason: "",
  });

  const [profile, setProfile] = useState({
    is_probation: false,
    is_notice_period: false,
    resignation_status: "",
    working_mode: "", // "Remote" | "In Office"
  });

  const [availability, setAvailability] = useState({
    available_wfh: 0,
  });

  const [message, setMessage] = useState("");

  /* ================= ELIGIBILITY ================= */

  const isRemote =
    (profile.working_mode || "").toLowerCase() === "remote";

  const isResignationApproved =
    (profile.resignation_status || "").toLowerCase() === "approved";

  const cannotApply =
    profile.is_probation ||
    profile.is_notice_period ||
    isRemote ||
    isResignationApproved ||
    availability.available_wfh === 0;

  /* ================= LOAD DATA ================= */

  useEffect(() => {
    const loadData = async () => {
      try {
        const [prof, avail] = await Promise.all([
          apiClient("/auth/user-info/"),
          apiClient("/wfh/availability/"),
        ]);

        setProfile({
          is_probation: !!prof?.is_probation,
          is_notice_period: !!prof?.is_notice_period,
          resignation_status: prof?.resignation_status || "",
          working_mode: prof?.working_mode || "",
        });

        setAvailability(avail || { available_wfh: 0 });
      } catch (err) {
        console.error(err);
        setMessage("Error loading WFH data.");
      }
    };

    loadData();
  }, []);

  /* ================= HANDLERS ================= */

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "startDate") {
      setWfhData({
        ...wfhData,
        startDate: value,
        endDate: value, // auto-lock end date
      });
      return;
    }

    setWfhData({ ...wfhData, [name]: value });
  };

  /* ================= APPLY WFH ================= */

  const handleApply = async () => {
    const { startDate, endDate, reason } = wfhData;
    setMessage("");

    if (!startDate || !endDate || !reason) {
      setMessage("All fields are required.");
      return;
    }

    if (startDate !== endDate) {
      setMessage("Only 1-day WFH is allowed.");
      return;
    }

    if (isRemote) {
      setMessage("Remote employees are not eligible for WFH.");
      return;
    }

    if (isResignationApproved) {
      setMessage("WFH is not allowed after resignation approval.");
      return;
    }

    if (cannotApply) {
      setMessage("You are not eligible to apply for WFH.");
      return;
    }

    try {
      await apiClient("/wfh/request/", {
        method: "POST",
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          reason,
        }),
      });

      setMessage("WFH request submitted successfully.");
      setWfhData({ startDate: "", endDate: "", reason: "" });
    } catch (err) {
      if (err?.error) {
        setMessage(err.error);
      } else if (err?.detail) {
        setMessage(err.detail);
      } else {
        setMessage("Network error submitting request.");
      }
    }
  };

  /* ================= UI ================= */

  return (
    <div className="apply-wfh">
      <h2>Apply Work From Home</h2>

      {/* ================= WARNINGS ================= */}

      {isRemote && (
        <div className="warning">
          You are a <b>REMOTE</b> employee — WFH is not applicable.
        </div>
      )}

      {profile.is_probation && (
        <div className="warning">
          You are under <b>PROBATION</b> — WFH not allowed.
        </div>
      )}

      {profile.is_notice_period && (
        <div className="warning">
          You are in <b>NOTICE PERIOD</b> — WFH not allowed.
        </div>
      )}

      {isResignationApproved && (
        <div className="warning">
          Your <b>RESIGNATION IS APPROVED</b> — WFH not allowed.
        </div>
      )}

      {availability.available_wfh === 0 && (
        <div className="warning">
          You have already used your <b>monthly WFH quota</b>.
        </div>
      )}

      {/* ================= FORM ================= */}

      <div className={`form-container ${cannotApply ? "disabled" : ""}`}>
        <div className="form-row">
          <div className="form-group">
            <label>WFH Date</label>
            <input
              type="date"
              name="startDate"
              value={wfhData.startDate}
              min={today}
              disabled={cannotApply}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>End Date (Auto locked)</label>
            <input
              type="date"
              name="endDate"
              value={wfhData.endDate}
              disabled
            />
          </div>
        </div>

        <div className="form-group">
          <label>Reason</label>
          <textarea
            name="reason"
            value={wfhData.reason}
            disabled={cannotApply}
            onChange={handleInputChange}
          />
        </div>

        {message && (
          <div
            className={`message ${
              message.toLowerCase().includes("not") ||
              message.toLowerCase().includes("error") ||
              message.toLowerCase().includes("already")
                ? "error"
                : "success"
            }`}
          >
            {message}
          </div>
        )}

        {!cannotApply && (
          <button className="apply-btn" onClick={handleApply}>
            Apply WFH
          </button>
        )}
      </div>

      <SessionHandler />
    </div>
  );
};

export default ApplyWorkFromHome;
